/* eslint-disable max-lines */
import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import {
  CHART_LINE_MARKER,
  DATE_AND_TIME_FORMAT,
  TRACKING
} from 'common/constants/constants';
import * as TrackingService from 'common/services/tracking.service';
import {
  calculatePercentage,
  formatUTCToTimezone,
  getTimezone,
  pluralize
} from 'common/utils/utils';
import { Flex } from 'components/base/layout-components';
import Text from 'components/base/Text';
import StyledAvatar from 'components/StyledAvatar';
import {
  getColor,
  getProjectsIssuesAreaColors
} from 'common/constants/_colors';
import { SPACES } from 'common/constants/sizes';
import { getContributorAvatarUrl } from 'common/constants/Avatars';
import { iterationTitle } from './projectExtended.services';
import {
  DELIVERY_STATES_API_KEYS,
  DELIVERY_STATES_KEYS,
  PROJECTS_PERIODS_OPTIONS,
  SCRUM_SORTED_CHART_KEYS,
  SCRUM_SORTED_ISSUES_KEYS,
  SHOW_DECIMAL_POINT_BELOW,
  SUMMARY_PERIODS
} from '../projects.consts';
import {
  createConfirmModalOptions,
  createEditProjectsModalOptions
} from 'modals/modal-options-factory';
import { checkProjectsUpdates } from 'common/services/pdt.service';
import { PROJECT_STATUS } from '@linearb/linjs-common';

const SYNC_INTERVAL = 1000 * 60;
export const getProjectConfigData = project => {
  const issuesChartType = project.issue_chart;
  const issuesChartConfig = PROJECTS.CHART_TYPES[issuesChartType];
  const cadenceType = _.get(project, 'cadence.type', null);
  const projectConfig =
    cadenceType && PROJECTS.TYPES[cadenceType] && PROJECTS.TYPES[cadenceType];
  return { issuesChartType, issuesChartConfig, projectConfig };
};

export const getProjectEstimationFields = (
  projectsEstimationFields,
  project
) => {
  const estimationDefault = 'story_points';

  const { investment_default, planning_default } = project;
  return {
    investment_type:
      _.get(
        projectsEstimationFields[project.id],
        'investment_type',
        investment_default
      ) || estimationDefault,
    delivery_type:
      _.get(
        projectsEstimationFields[project.id],
        'delivery_type',
        planning_default
      ) || estimationDefault
  };
};

export const updatedAt = lastUpdated => {
  return `${formatUTCToTimezone(
    moment(lastUpdated).format(DATE_AND_TIME_FORMAT),
    DATE_AND_TIME_FORMAT
  )} ${getTimezone()}`;
};

export const getPeriodDisplayValue = selectedPeriod => {
  return _.get(
    getProjectsPeriodsOptions(false).find(
      ({ value }) => value === selectedPeriod
    ),
    'displayValue',
    ''
  );
};

export const getActivePeopleTitle = (
  selectedPeriod,
  iterationsLength,
  isSingleIterationView,
  singleIteration
) => {
  if (isSingleIterationView) return iterationTitle(singleIteration);

  return ` ${getPeriodDisplayValue(selectedPeriod)} - ${pluralize(
    iterationsLength,
    'iteration'
  )}`;
};

export const getProjectsPeriodsOptions = (
  isExtendedView = false,
  lastIteration,
  selectedIteration
) => {
  const projectsPeriodsOptions = PROJECTS_PERIODS_OPTIONS(
    lastIteration,
    selectedIteration
  );
  const projectsPeriodsOptionsWithoutSingleIteration =
    projectsPeriodsOptions.filter(
      ({ value }) => value !== SUMMARY_PERIODS.single_iteration
    );

  return isExtendedView && selectedIteration
    ? projectsPeriodsOptions
    : projectsPeriodsOptionsWithoutSingleIteration;
};

export const selectedPeriodOnChange = ({
  selected,
  setLastSelectedPeriod,
  setSelectedPeriod,
  setIterationIndex
}) => {
  if (!['I', 'L'].includes(selected)) {
    setLastSelectedPeriod(selected);
  }
  setSelectedPeriod(selected);
  setIterationIndex(null);
};

export const getAreaChartColor = (key, color) => {
  return color || _.get(DELIVERY_STATES_KEYS, `${[key]}.color`);
};

export const getDeliveryChartSeries = (
  size = 'small',
  projectId,
  issuesData,
  type,
  isLastIteration,
  isScrumProject,
  projectsExcludedIssues = {}
) => {
  const CHART_CONFIG = PROJECTS.CHART_TYPES[type];
  const SECONDARY_CHART = CHART_CONFIG.secondaryChart || {};
  const SECONDARY_CHART_ORDER = CHART_CONFIG.secondaryChartOrder || [];

  const barWidth = 15;

  const calcPlanningMarkerWidth = (size, length) => {
    const planningMarkerWidth =
      size === 'small'
        ? parseInt(parseInt(PROJECTS.SMALL_CHART_SIZE.width) / (length * 2))
        : parseInt(
            (parseInt(PROJECTS.LARGE_CHART_SIZE.width) - 40) / (length * 2) // 40 = large chart padding
          );

    return planningMarkerWidth < barWidth ? planningMarkerWidth : barWidth;
  };

  const chartSeries = issuesData
    .filter(({ key }) => key !== DELIVERY_STATES_API_KEYS.UNCOMPLETED)
    .map(({ key, values, color, iterations = [] }) => {
      const planningMarkerWidth = calcPlanningMarkerWidth(
        size,
        iterations.length
      );

      const barColor =
        key === DELIVERY_STATES_API_KEYS.ADDED &&
        size !== 'small' &&
        isScrumProject
          ? getColor('waikawaGray')
          : getColor(getAreaChartColor(key, color));

      return {
        name: key,
        data: values,
        iterations,
        color: barColor,
        fillColor: barColor,
        lineWidth: 0,
        // configuration for combined stacked columns + line

        ...(SECONDARY_CHART.type === 'line'
          ? key === SECONDARY_CHART.key
            ? {
                marker: {
                  enabled: true,
                  symbol: CHART_LINE_MARKER,
                  width: planningMarkerWidth,
                  height: 2
                },
                type: 'line',
                lineWidth: 0,
                lineColor: 'transparent'
              }
            : {
                type: PROJECTS.CHART_TYPES[type].chart,
                stack: 1,
                borderWidth: 0,
                yAxis: 0
              }
          : {
              type: isLastIteration
                ? PROJECTS.CHART_TYPES[type].lastIterationChart
                : PROJECTS.CHART_TYPES[type].chart
            }),
        pointWidth: isLastIteration ? barWidth : null,
        maxPointWidth: barWidth,
        states: {
          hover: {
            enabled: false
          }
        }
      };
    });

  const sortedCartSeries = _.sortBy(chartSeries, ({ name }) =>
    SECONDARY_CHART_ORDER.length
      ? SECONDARY_CHART_ORDER.indexOf(name)
      : name === SECONDARY_CHART.key
  );

  // remove filtered issues from chart by setting data to empty
  return !projectsExcludedIssues || !projectsExcludedIssues[projectId]
    ? sortedCartSeries
    : sortedCartSeries.map(item =>
        projectsExcludedIssues[projectId].includes(item.name)
          ? { ...item, data: [] }
          : item
      );
};

export const getPieItem = (category, value, total, investmentColorsMap) => {
  const percent = calculatePercentage(value, total);

  return {
    name: category,
    value: isNaN(percent) ? 0 : percent,
    color: getColor(investmentColorsMap[category])
  };
};

export const getContributorName = name => {
  return !name || name === 'Unknown User' ? (
    <Text size='medium' italic color='silver'>
      Missing name
    </Text>
  ) : (
    name
  );
};

export const getContributorAvatarAndName = (id, name, width = '100%') => {
  return (
    <>
      <StyledAvatar
        size='2rem'
        avatar={{
          url: getContributorAvatarUrl({ id: id?.replace(/\D/g, '') }),
          name: null
        }}
      />
      <Flex width={width}>
        <Text
          size='medium'
          pl={SPACES['x-small']}
          pr={SPACES.small}
          width='13rem'
          ellipsisAfter='13rem'
        >
          {getContributorName(name)}
        </Text>
      </Flex>
    </>
  );
};

export const getOrderedPeopleEffort = people => {
  return _.orderBy(people, ['effort', 'name'], ['desc', 'asc']);
};

export const isAllObjectValuesZero = object => {
  return Object.values(object || {}).filter(Boolean).length === 0;
};

export const showDecimal = val => {
  return val < SHOW_DECIMAL_POINT_BELOW ? 1 : 0;
};

export const PROJECTS = {
  SECONDARY_COLOR: 'osloGray',
  CHART_STYLE: { fontSize: '8px' },
  MAX_TOP_DELIVERY_TYPES: 4,
  LARGE_PIE_SIZE: { width: 60, height: 60, line: 9 },
  MEDIUM_PIE_SIZE: { width: 60, height: 60, line: 9 },
  SMALL_PIE_SIZE: { width: 30, height: 30, line: 5 },
  SMALL_CHART_SIZE: { width: '120px', height: '80px' },
  SMALL_BAR_SIZE: { width: '120px', height: '40px' },
  LARGE_CHART_SIZE: { width: '580px', height: '100px' },
  ITERATIONS_TABLE_SIZE: { width: '52rem' },
  SMALL_CHART_MARGIN: [0, 0, 1, 0],
  LARGE_CHART_MARGIN: [10, 0, 10, 40],
  CHART_TYPES: {
    'planned/executed': {
      chart: 'column',
      lastIterationChart: 'column',
      secondaryChartOrder: SCRUM_SORTED_CHART_KEYS,
      sortedKeys: SCRUM_SORTED_ISSUES_KEYS,
      secondaryChart: {
        type: 'line',
        key: _.get(DELIVERY_STATES_API_KEYS, 'PLANNED')
      },
      title: 'Planned vs. Executed',
      issuesSorted: ({ issuesData }) => {
        const issues = issuesData.filter(
          ({ key }) => key !== DELIVERY_STATES_API_KEYS.ADDED_UNCOMPLETED
        );

        return issues.sort((itema, itemb) => {
          if (
            SCRUM_SORTED_ISSUES_KEYS.indexOf(itema.key) <
            SCRUM_SORTED_ISSUES_KEYS.indexOf(itemb.key)
          )
            return -1;
          else return 1;
        });
      },
      issuesPie: ({ issuesData }) => {
        return issuesData.filter(
          ({ key }) => key !== DELIVERY_STATES_API_KEYS.PLANNED
        );
      }
    },
    CFD: {
      chart: 'area',
      lastIterationChart: 'column',
      title: 'Cumulative flow diagram',
      pieTitle: 'Delivery',
      issuesSorted: ({ issuesData }) => {
        return _.orderBy(
          issuesData.filter(
            ({ category }) => category !== DELIVERY_STATES_API_KEYS.DONE
          ),
          ['value', 'key'],
          ['desc', 'desc']
        )
          .slice(0, PROJECTS.MAX_TOP_DELIVERY_TYPES)
          .map((item, index) => {
            return { color: getProjectsIssuesAreaColors()[index], ...item };
          })
          .reverse()
          .concat(
            issuesData
              .filter(
                ({ category }) => category === DELIVERY_STATES_API_KEYS.DONE
              )
              .map(item => {
                return {
                  color:
                    DELIVERY_STATES_KEYS[DELIVERY_STATES_API_KEYS.DONE].color,
                  ...item
                };
              })
          );
      },
      issuesPie: ({ issuesData }) => {
        return issuesData;
      }
    }
  },
  TYPES: {
    Scrum: {
      icon: 'Scrum',
      title: 'Scrum',
      showDeliveryPie: true,
      showVelocityPie: true,
      avgPerDuration: 'iteration'
    },
    Kanban: {
      icon: 'Kanban',
      title: 'Kanban',
      showDeliveryPie: false,
      showVelocityPie: false,
      avgPerDuration: 'week',
      description: 'weekly'
    }
  }
};

/* istanbul ignore next */
export const onEdit = ({
  toggleModal,
  project,
  pmSensorAuthId,
  onSaveClick
} = {}) => {
  TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
    page: TRACKING.PAGES.PROJECTS,
    click_type: TRACKING.CLICK.EDIT_PROJECT,
    project_name: _.get(project, 'name')
  });

  toggleModal(
    createEditProjectsModalOptions({
      projectName: _.get(project, 'name'),
      projectId: _.get(project, 'id'),
      pmSensorAuthId,
      onSaveClick
    })
  );
};

/* istanbul ignore next */
export const onDelete = ({
  toggleModal,
  toggleSnackBar,
  project,
  deleteProject,
  onDeleteProject = _.noop,
  deleteCallback = _.noop
} = {}) => {
  TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
    page: TRACKING.PAGES.PROJECTS,
    click_type: TRACKING.CLICK.DELETE_PROJECT,
    project_name: _.get(project, 'name')
  });

  toggleModal(
    createConfirmModalOptions({
      title: 'Delete Project',
      description: `Are you sure you want to delete project - ${_.get(
        project,
        'name'
      )}?`,
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await deleteProject(_.get(project, 'id'));
          toggleSnackBar({
            message: (
              <Flex direction='column'>
                <Text size='large' color='white'>
                  Project deleted successfully
                </Text>
              </Flex>
            ),
            variant: 'success'
          });
          toggleModal();
          onDeleteProject(project);
          deleteCallback();
        } catch (e) {}
      },
      onCancel: () => toggleModal()
    })
  );
};

export const checkStatusChanges = ({
  projects = [],
  onChange,
  selectedPeriod
}) => {
  const nonSteadyProjectsIds = projects
    .filter(item => _.get(item, 'status') !== PROJECT_STATUS.STEADY)
    .map(item => item.id);
  if (!nonSteadyProjectsIds.length) return null;
  return setInterval(async () => {
    const result = await checkProjectsUpdates({
      projects_ids: nonSteadyProjectsIds,
      duration_filter: selectedPeriod
    });
    if (result?.length) {
      const mapResult = result.reduce((map, obj) => {
        map[obj.id] = obj;
        return map;
      }, {});
      onChange(mapResult);
    }
  }, SYNC_INTERVAL);
};

export const getRangeByValue = (value, ranges) => {
  if (isNaN(value) || value < 0) {
    value = 0;
  }

  // ranges type CAPACITY_RANGES or PLANNED_RANGES
  const maxValues = ranges.map(({ range = {} }) => range.max);
  const maxValue = _.max(maxValues);

  if (value > maxValue) {
    value = maxValue;
  }

  return ranges.find(
    ({ range: { min, max } = {} }) => value >= min && value <= max
  );
};
